import { useCallback, useEffect, useRef, useState } from 'react';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Field from '../../components/Field';
import Select from '../../components/Select';
import Callout from '../../components/Callout';
import MultiSelect from '../../components/MultiSelect';
import {
  MASSING,
  ROOF,
  MATERIAL,
  GLAZING,
  ENTRY,
  PALETTE,
  ERA,
  SIGNATURE,
} from '../../data/style-library';
import { VERTICALS } from '../../data/verticals';
import { cx } from '../../lib/cx';

/** The eight categorical axes, in the order they are easiest to judge from a
 *  photograph: shape first, then surface, then detail. */
const AXES = [
  { key: 'massing', label: 'Massing', vocab: MASSING },
  { key: 'roof', label: 'Roof', vocab: ROOF },
  { key: 'primaryMaterial', label: 'Primary material', vocab: MATERIAL },
  { key: 'accentMaterial', label: 'Accent material', vocab: MATERIAL, optional: true },
  { key: 'glazing', label: 'Glazing', vocab: GLAZING },
  { key: 'entry', label: 'Entry', vocab: ENTRY },
  { key: 'palette', label: 'Palette', vocab: PALETTE },
  { key: 'era', label: 'Era', vocab: ERA },
  { key: 'signature', label: 'Signature element', vocab: SIGNATURE },
] as const;

const SCALES = [
  { key: 'ornament', label: 'Ornament', low: 'Plain', high: 'Highly detailed' },
  { key: 'verticality', label: 'Verticality', low: 'Low and horizontal', high: 'Tall and vertical' },
  { key: 'symmetry', label: 'Symmetry', low: 'Asymmetric', high: 'Mirror-symmetrical' },
] as const;

type Tags = Record<string, string | number>;

const EMPTY_TAGS: Tags = {
  massing: '',
  roof: '',
  primaryMaterial: '',
  accentMaterial: '',
  glazing: '',
  entry: '',
  palette: '',
  era: '',
  signature: '',
  ornament: 2,
  verticality: 2,
  symmetry: 2,
};

interface AdminImage {
  id: string;
  verticalIds: string[];
  url: string;
  credit: string;
  caption: string | null;
  tags: Record<string, unknown>;
  active: boolean;
  /** Model-proposed, never authoritative. See worker/tagger.ts. */
  suggestedTags?: Record<string, unknown> | null;
  suggestedModel?: string | null;
  suggestedAt?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  photographer?: string | null;
  license?: string | null;
}

/** An image is only usable once every required axis has a value. */
const REQUIRED_AXES = AXES.filter((a) => !('optional' in a && a.optional)).map((a) => a.key);
const isTagged = (img: AdminImage) => REQUIRED_AXES.every((k) => Boolean(img.tags?.[k]));

/** Numeric axes as five buttons. A slider is a poor target and a poor record of
 *  a judgement that only has five values. */
function ScaleRow({
  label,
  low,
  high,
  value,
  onChange,
}: {
  label: string;
  low: string;
  high: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <p className="t-h3 text-ink">{label}</p>
      <div className="mt-2 flex gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={value === n}
            aria-label={`${label} ${n} of 4`}
            onClick={() => onChange(n)}
            className={cx(
              'min-h-tap flex-1 rounded-sm border text-[0.95rem] font-semibold transition-colors',
              value === n
                ? 'border-green-600 bg-green-050 text-green-900 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
                : 'border-grey-300 bg-white text-ink hover:bg-grey-050',
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <p className="t-meta mt-1 flex justify-between text-grey-600">
        <span>{low}</span>
        <span>{high}</span>
      </p>
    </div>
  );
}

export default function AdminStyles() {
  const [images, setImages] = useState<AdminImage[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dims, setDims] = useState<{ width: number; height: number } | null>(null);
  const [verticalIds, setVerticalIds] = useState<string[]>([]);
  const [credit, setCredit] = useState('');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState<Tags>(EMPTY_TAGS);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminImage | null>(null);
  const [editTags, setEditTags] = useState<Tags>(EMPTY_TAGS);
  const [savingEdit, setSavingEdit] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [usedSuggestion, setUsedSuggestion] = useState(false);
  const [filter, setFilter] = useState<'untagged' | 'active' | 'all'>('untagged');
  const [stats, setStats] = useState<{ id: string; picks: number; url: string }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Access is the session cookie now; the interim token gate is gone.
  const load = useCallback(async () => {
    const res = await fetch('/api/admin/styles');
    if (!res.ok) return;
    const body = (await res.json()) as { images: AdminImage[] };
    setImages(body.images);

    // Pick analytics (CLAUDE.md §9.9). Best-effort: the library page is still
    // useful if this fails.
    try {
      const s = await fetch('/api/admin/styles/stats');
      if (s.ok) setStats(((await s.json()) as { stats: typeof stats }).stats);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    // Measured here rather than in the Worker: the browser already decodes the
    // file for the preview, and the picker needs the ratio to reserve space.
    const probe = new Image();
    probe.onload = () => setDims({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const missing = AXES.filter((a) => !('optional' in a && a.optional) && !tags[a.key]).map(
    (a) => a.label,
  );
  const canSave =
    Boolean(file) && verticalIds.length > 0 && credit.trim().length > 2 && missing.length === 0;

  const upload = async () => {
    if (!file || !canSave) return;
    setSaving(true);
    setError(null);
    setMessage(null);

    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(tags)) if (v !== '') clean[k] = v;

    const form = new FormData();
    form.append('file', file);
    form.append(
      'meta',
      JSON.stringify({
        verticalIds,
        credit: credit.trim(),
        caption: caption.trim() || undefined,
        tags: clean,
        width: dims?.width,
        height: dims?.height,
      }),
    );

    const res = await fetch('/api/admin/styles', { method: 'POST', body: form });
    setSaving(false);
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      setError(body?.error ?? 'That upload did not go through.');
      return;
    }

    // Keep the tags and verticals; the next photo in a batch is usually similar,
    // and re-picking nine axes from scratch every time is what makes tagging
    // fifty images feel like a day's work instead of an hour's.
    setFile(null);
    setDims(null);
    setCaption('');
    if (fileRef.current) fileRef.current.value = '';
    setMessage('Saved. Tags kept for the next photo — change what differs.');
    void load();
  };

  /**
   * Bulk ingest writes rows with empty tags, deliberately inactive. This is
   * where they become usable: tag the nine axes, then activate. Activating an
   * untagged image is blocked, because it would consume one of a customer's
   * five to ten picks and contribute nothing to their profile.
   */
  const openEditor = (img: AdminImage) => {
    setEditing(img);
    // Suggestions fill the form only where a person has not already decided.
    // Real tags always win: reopening a tagged image must show what was saved,
    // not what a model would have said.
    setEditTags({
      ...EMPTY_TAGS,
      ...((img.suggestedTags ?? {}) as Tags),
      ...(img.tags as Tags),
    });
    setUsedSuggestion(Boolean(img.suggestedTags) && !isTagged(img));
    setMessage(null);
    setError(null);
  };

  /**
   * Pre-tag the next few untagged images.
   *
   * Bounded and repeated rather than one long pass: it bills per image, the
   * Worker has a time limit, and stopping halfway through an unbounded job
   * leaves no record of where it got to.
   */
  const suggestNext = async () => {
    setSuggesting(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/styles/suggest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ limit: 4 }),
      });
      if (!res.ok) {
        setError(
          res.status === 503
            ? 'Workers AI is not available on this deployment.'
            : 'The suggestion pass did not run.',
        );
        return;
      }
      const out = (await res.json()) as {
        suggested: number; failed: number; remaining: number;
      };
      setMessage(
        `Suggested ${out.suggested}${out.failed ? `, ${out.failed} could not be read` : ''}. ` +
          `${out.remaining} left with no suggestion.`,
      );
      void load();
    } finally {
      setSuggesting(false);
    }
  };

  const saveTags = async (alsoActivate: boolean) => {
    if (!editing) return;
    setSavingEdit(true);
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(editTags)) if (v !== '') clean[k] = v;

    const res = await fetch(`/api/admin/styles/${editing.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tags: clean, ...(alsoActivate ? { active: true } : {}) }),
    });
    setSavingEdit(false);
    if (!res.ok) {
      setError('Those tags did not save.');
      return;
    }
    setEditing(null);
    setMessage(alsoActivate ? 'Tagged and live in the picker.' : 'Tags saved. Still inactive.');
    void load();
  };

  const toggleActive = async (img: AdminImage) => {
    await fetch(`/api/admin/styles/${img.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active: !img.active }),
    });
    void load();
  };

  const placeholders = images.filter((i) => i.credit.startsWith('PLACEHOLDER'));

  return (
    <AppShell action={<span className="t-meta text-grey-600">Style library</span>}>
      <p className="t-eyebrow text-green-600">Admin</p>
      <h1 className="t-h1 mt-3 text-ink">Style library</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Every photo is tagged by hand across nine axes. Those tags are what the whole
        product runs on, so they are worth the time.
      </p>

      {placeholders.length > 0 && (
        <div className="mt-8">
          <Callout tone="caution" title={`${placeholders.length} placeholder images are live`}>
            These are generated stand-ins for development, not real photography. Deactivate
            or delete them before any customer sees the picker.
          </Callout>
        </div>
      )}

      <section className="mt-12 rounded-lg border border-grey-200 bg-white p-6 shadow-sm sm:p-8">
        <h2 className="t-h2 text-ink">Add a photo</h2>

        <div className="mt-6 grid gap-8 lg:grid-cols-[320px_1fr]">
          <div>
            <label htmlFor="admin-file" className="t-h3 block text-ink">
              Photograph
            </label>
            <input
              ref={fileRef}
              id="admin-file"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full text-[0.95rem] file:mr-4 file:min-h-tap file:rounded-full file:border-0 file:bg-green-600 file:px-5 file:font-semibold file:text-white"
            />
            {preview && (
              <img
                src={preview}
                alt=""
                className="mt-4 w-full rounded-sm border border-grey-200"
              />
            )}

            <div className="mt-6">
              <MultiSelect
                legend="Which building types?"
                hint="One photo can serve several. The first one decides where the file is stored."
                options={VERTICALS.map((v) => ({ value: v.id, label: v.label }))}
                value={verticalIds}
                onChange={setVerticalIds}
              />
            </div>

            <div className="mt-6 flex flex-col gap-6">
              <Field
                id="admin-credit"
                label="Credit and license"
                hint="Required. Where it came from and what permits this use."
                placeholder="Etrenzik Design project photo, 2025"
                value={credit}
                onChange={(e) => setCredit(e.target.value)}
              />
              <Field
                id="admin-caption"
                label="Caption"
                hint="Optional, descriptive, never evaluative. Shown when a customer enlarges it."
                placeholder="Brick sanctuary with a projecting entry tower"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
              />
            </div>
          </div>

          <div>
            <h3 className="t-h3 text-ink">Tags</h3>
            <p className="t-meta measure mt-1 text-grey-600">
              Judge what the photograph shows, not what the building is called.
            </p>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              {AXES.map((axis) => (
                <Select
                  key={axis.key}
                  id={`tag-${axis.key}`}
                  label={axis.label + ('optional' in axis && axis.optional ? ' (optional)' : '')}
                  placeholder="Choose one"
                  options={Object.entries(axis.vocab).map(([value, label]) => ({
                    value,
                    label: label as string,
                  }))}
                  value={String(tags[axis.key] ?? '')}
                  onChange={(e) => setTags((t) => ({ ...t, [axis.key]: e.target.value }))}
                />
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-6">
              {SCALES.map((s) => (
                <ScaleRow
                  key={s.key}
                  label={s.label}
                  low={s.low}
                  high={s.high}
                  value={Number(tags[s.key] ?? 2)}
                  onChange={(v) => setTags((t) => ({ ...t, [s.key]: v }))}
                />
              ))}
            </div>
          </div>
        </div>

        {missing.length > 0 && file && (
          <div className="mt-8">
            <Callout title="Still to tag">{missing.join(', ')}</Callout>
          </div>
        )}
        {error && (
          <div className="mt-8">
            <Callout tone="caution" title="That did not save">
              {error}
            </Callout>
          </div>
        )}
        {message && (
          <div className="mt-8">
            <Callout tone="confirm" title="Saved">
              {message}
            </Callout>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button onClick={() => void upload()} disabled={!canSave || saving}>
            {saving ? 'Uploading…' : 'Save this photo'}
          </Button>
          <Button variant="quiet" onClick={() => setTags(EMPTY_TAGS)}>
            Clear the tags
          </Button>
        </div>
      </section>

      {stats.length > 0 && (
        <section className="mt-14">
          <h2 className="t-h2 text-ink">Picked most often</h2>
          <p className="t-meta measure mt-2 text-grey-600">
            What customers actually respond to. Over a year this is a real asset — it tells
            you what the Southeast market wants, and it is the cheapest guide there is to
            what to photograph next.
          </p>
          <ul className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
            {stats.slice(0, 12).map((s) => (
              <li key={s.id} className="overflow-hidden rounded-lg border border-grey-200 bg-white shadow-sm">
                <img src={s.url} alt="" className="block w-full" loading="lazy" />
                <p className="t-meta px-2 py-2 text-grey-600">
                  <span className="t-numeral font-semibold text-ink">{s.picks}</span>{' '}
                  {s.picks === 1 ? 'pick' : 'picks'}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-14">
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <h2 className="t-h2 text-ink">
            In the library <span className="t-numeral text-grey-600">({images.length})</span>
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {images.some((i) => !isTagged(i) && !i.suggestedTags) && (
              <button
                type="button"
                onClick={() => void suggestNext()}
                disabled={suggesting}
                className="t-meta min-h-tap inline-flex items-center rounded-full border border-green-600 bg-green-050 px-5 font-semibold text-green-900 disabled:opacity-50"
              >
                {suggesting ? 'Reading photographs…' : 'Pre-fill the next 4'}
              </button>
            )}
            {(
              [
                ['untagged', `Needs tagging (${images.filter((i) => !isTagged(i)).length})`],
                ['active', `Live (${images.filter((i) => i.active).length})`],
                ['all', 'All'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                aria-pressed={filter === k}
                onClick={() => setFilter(k)}
                className={
                  filter === k
                    ? 'min-h-tap rounded-full border border-green-600 bg-green-050 px-5 text-[0.95rem] font-semibold text-green-900'
                    : 'min-h-tap rounded-full border border-grey-300 bg-white px-5 text-[0.95rem] text-ink hover:bg-grey-050'
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <ul className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {images
            .filter((i) =>
              filter === 'untagged' ? !isTagged(i) : filter === 'active' ? i.active : true,
            )
            .map((img) => (
              <li
                key={img.id}
                className={cx(
                  'overflow-hidden rounded-lg border bg-white shadow-sm',
                  img.active ? 'border-grey-200' : 'border-grey-300',
                )}
              >
                <img
                  src={img.url}
                  alt=""
                  className={cx('block w-full', !img.active && 'opacity-55')}
                  loading="lazy"
                />
                <div className="p-3">
                  <p className="t-meta text-grey-600">{img.credit}</p>
                  {img.sourceUrl && (
                    <a
                      href={img.sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="t-meta mt-1 block text-green-700 underline decoration-1 underline-offset-4"
                    >
                      Source
                    </a>
                  )}
                  <p className="t-meta mt-1 font-semibold text-grey-600">
                    {isTagged(img) ? (img.active ? 'Live' : 'Tagged, inactive') : 'Needs tagging'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => openEditor(img)}
                      className="t-meta min-h-tap font-semibold text-green-700 underline decoration-1 underline-offset-4"
                    >
                      {isTagged(img) ? 'Edit tags' : 'Tag it'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void toggleActive(img)}
                      disabled={!img.active && !isTagged(img)}
                      title={
                        !img.active && !isTagged(img) ? 'Tag it before it can go live' : undefined
                      }
                      className="t-meta min-h-tap font-semibold text-green-700 underline decoration-1 underline-offset-4 disabled:cursor-not-allowed disabled:text-grey-500 disabled:no-underline"
                    >
                      {img.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              </li>
            ))}
        </ul>
      </section>

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Tag this photograph"
          className="fixed inset-0 z-50 overflow-y-auto bg-ink/70 p-4 sm:p-8"
        >
          <div className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow-lg sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <h2 className="t-h2 text-ink">Tag this photograph</h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="min-h-tap rounded-full border border-grey-300 px-5 text-[0.95rem] text-ink"
              >
                Cancel
              </button>
            </div>

            {usedSuggestion && (
              <div className="mt-5 rounded-lg border border-green-600 bg-green-050 p-4">
                <p className="t-meta font-semibold text-green-900">
                  Pre-filled by a model. Nothing is saved until you save it.
                </p>
                <p className="t-meta mt-1 text-green-900">
                  Check every axis against the photograph — a wrong tag is invisible
                  here and only shows up later as a brief that does not match what the
                  customer picked. Axes the model could not judge are left blank.
                </p>
              </div>
            )}

            <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
              <div>
                <img
                  src={editing.url}
                  alt=""
                  className="w-full rounded-sm border border-grey-200"
                />
                <p className="t-meta mt-3 text-grey-600">{editing.credit}</p>
                {editing.caption && (
                  <p className="t-meta mt-1 text-grey-600">{editing.caption}</p>
                )}
              </div>

              <div>
                <div className="grid gap-5 sm:grid-cols-2">
                  {AXES.map((axis) => (
                    <Select
                      key={axis.key}
                      id={`edit-${axis.key}`}
                      label={
                        axis.label + ('optional' in axis && axis.optional ? ' (optional)' : '')
                      }
                      placeholder="Choose one"
                      options={Object.entries(axis.vocab).map(([value, label]) => ({
                        value,
                        label: label as string,
                      }))}
                      value={String(editTags[axis.key] ?? '')}
                      onChange={(e) => setEditTags((t) => ({ ...t, [axis.key]: e.target.value }))}
                    />
                  ))}
                </div>
                <div className="mt-7 flex flex-col gap-6">
                  {SCALES.map((sc) => (
                    <ScaleRow
                      key={sc.key}
                      label={sc.label}
                      low={sc.low}
                      high={sc.high}
                      value={Number(editTags[sc.key] ?? 2)}
                      onChange={(v) => setEditTags((t) => ({ ...t, [sc.key]: v }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 border-t border-grey-200 pt-6">
              <Button
                onClick={() => void saveTags(true)}
                disabled={savingEdit || REQUIRED_AXES.some((k) => !editTags[k])}
              >
                {savingEdit ? 'Saving…' : 'Save and put it live'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => void saveTags(false)}
                disabled={savingEdit}
              >
                Save, keep it inactive
              </Button>
            </div>
          </div>
        </div>
      )}

    </AppShell>
  );
}
